<template>
  <b-modal id="orders-modal" :title="$t('menu.orders', 'Orders')" size="lg" ok-only>
    <template #modal-title>
      <div class="orders-modal-title">
        <span class="title-label">
          {{ $t('Transactions') }}
        </span>
        <span class="title-meta">
          {{ $t('Orders history') }}
        </span>
      </div>
    </template>
    <div class="modal-section">
      <template v-if="!isMobile">
        <b-table
            size="sm"
            :items="paginatedOrders"
            :fields="fields"
            head-variant="light"
            responsive="sm"
            hover
            class="styled-table"
        >
          <template #cell(status)="row">
            <span :class="['order-status', getStatusVariant(row.item.status)]">
              {{ row.item.status }}
            </span>
          </template>
        </b-table>
      </template>
      <template v-else>
        <div class="orders-mobile-list">
          <div
              v-for="order in paginatedOrders"
              :key="order.id"
              class="order-card"
          >
            <div class="order-card-header">
              <span class="order-number">Order #{{ order.id }}</span>
              <span :class="['order-status', getStatusVariant(order.status)]">
                {{ order.status }}
              </span>
            </div>
            <div class="order-card-body">
              <div class="order-row">
                <span class="order-label">Date</span>
                <span class="order-value">{{ order.date }}</span>
              </div>
              <div class="order-row">
                <span class="order-label">Payment</span>
                <span class="order-value">{{ order.type }}</span>
              </div>
              <div class="order-row">
                <span class="order-label">Price</span>
                <span class="order-value order-price">{{ order.price }}</span>
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>

    <b-pagination
        v-model="currentPage"
        :total-rows="formattedOrders.length"
        :per-page="perPage"
        align="center"
        class="mt-3 styled-pagination"
        size="sm"
    />
    <div class="text-center mt-3">
      <b-link class="table-link" @click="goToTransactions">
        Go to transactions
      </b-link>
    </div>
  </b-modal>
</template>

<script>
export default {
  props: {
    orders: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      fields: [
        {key: 'id', label: 'ID'},
        {key: 'date', label: 'Date'},
        {key: 'type', label: 'Payment Method'},
        {key: 'status', label: 'Status'},
        {key: 'price', label: 'Price'}
      ],
      currentPage: 1,
      perPage: 10,
      windowWidth: window.innerWidth
    }
  },
  computed: {
    // Проверка, является ли устройство мобильным
    isMobile() {
      return this.windowWidth < 768
    },
    // Форматирование список заказов с преобразованием валюты и статусов
    formattedOrders() {
      return this.orders.map(order => {
        // Форматирование валюты
        let currencySymbol = '';
        if (order.price && order.price.currency) {
          switch (order.price.currency) {
            case 'RUB':
              currencySymbol = '₽';
              break;
            case 'USD':
              currencySymbol = '$';
              break;
            case 'EUR':
              currencySymbol = '€';
              break;
            default:
              currencySymbol = order.price.currency;
          }
        }

        // Форматирование статуса заказа
        let displayStatus = '';
        if (order.status) {
          switch (order.status) {
            case 'pre_order':
              displayStatus = "request";
              break;
            case 'owner_approved':
            case 'new':
              displayStatus = "to pay";
              break;
            case 'paid':
            case 'ready_to_publish':
            case 'awaiting_charts':
            case 'adding_charts':
              displayStatus = "processing";
              break;
            case 'download_charts':
              displayStatus = "downloading"
              break;
            case 'complete':
              displayStatus = "complete";
              break;
            case 'cancelled_by_distirbutor':
              displayStatus = "cancelled";
              break;
            case 'error':
              displayStatus = "error";
              break;
            default:
              displayStatus = order.status;
          }
        }

        return {
          id: order.displayId || '',
          date: order.updatedAt ? new Date(order.updatedAt).toLocaleDateString() : '',
          type: order.paymentMethod?.paymentMethod || '',
          status: displayStatus,
          price: order.price ? `${order.price.price} ${currencySymbol}` : ''
        };
      });
    },
    // Пагинация для отображения только части заказов
    paginatedOrders() {
      const start = (this.currentPage - 1) * this.perPage;
      const end = start + this.perPage;
      return this.formattedOrders.slice(start, end);
    }
  },
  mounted() {
    // Установка слушателя события изменения размера окна
    window.addEventListener('resize', this.handleResize);
  },
  beforeDestroy() {
    // Удаление слушателя события изменения размера окна
    window.removeEventListener('resize', this.handleResize);
  },
  methods: {
    // Обработчик изменения размера окна
    handleResize() {
      this.windowWidth = window.innerWidth;
      this.$forceUpdate();
    },
    // Переход к странице транзакций
    goToTransactions() {
      this.$bvModal.hide('orders-modal');
      this.$router.push('/transactions');
    },
    // Получение варианта статуса для отображения в виде беджа
    getStatusVariant(status) {
      switch (status.toLowerCase()) {
        case 'complete':
          return 'success';
        case 'processing':
          return 'primary';
        case 'to pay':
          return 'warning';
        case 'request':
          return 'info';
        case 'cancelled':
        case 'error':
          return 'danger';
        default:
          return 'secondary';
      }
    },
  },
}
</script>

<style scoped>
.modal-section {
  border: 1px solid #e4e8f5;
  border-radius: 12px;
  padding: 16px;
  background: linear-gradient(135deg, #fbfcff 0%, #f6f8ff 100%);
  box-shadow: 0 4px 16px rgba(34, 45, 80, 0.07);
  font-family: 'Roboto', 'Helvetica Neue', 'Segoe UI', sans-serif;
}

.styled-table >>> thead tr {
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-size: 0.72rem;
  color: #5c6685;
  background: #eef1fb;
}

.styled-table >>> tbody tr {
  transition: transform 0.18s ease, box-shadow 0.18s ease;
}

.styled-table >>> tbody tr:hover {
  transform: translateY(-1px);
  box-shadow: 0 10px 18px rgba(15, 23, 42, 0.09);
  background: #ffffff;
}

.styled-table >>> td {
  vertical-align: middle;
  padding: 0.75rem;
  border-top: none;
  font-family: 'Roboto', 'Helvetica Neue', 'Segoe UI', sans-serif;
  font-size: 0.78rem;
  font-weight: 500;
  color: #1f2940;
}

.styled-pagination >>> .page-link {
  border-radius: 999px !important;
  margin: 0 4px;
  padding: 6px 12px;
  border: 1px solid rgba(98, 111, 149, 0.25);
  background: rgba(255, 255, 255, 0.95);
  color: #1f2940;
  font-family: 'Roboto', 'Helvetica Neue', 'Segoe UI', sans-serif;
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  transition: all 0.18s ease;
  box-shadow: 0 1px 4px rgba(20, 29, 56, 0.08);
}

.styled-pagination >>> .page-item.active .page-link {
  background: linear-gradient(135deg, #f4f6ff, #e9edff);
  color: #2f3760;
  border-color: rgba(98, 111, 149, 0.35);
  box-shadow: 0 6px 14px rgba(31, 41, 64, 0.16);
}

.styled-pagination >>> .page-link:hover {
  color: #151b2c;
  box-shadow: 0 4px 10px rgba(31, 41, 64, 0.12);
}

.styled-pagination >>> .page-item.disabled .page-link {
  opacity: 0.5;
  box-shadow: none;
}

.table-link {
  font-weight: 500;
  color: #3a4256;
  border-bottom: 1px dashed rgba(37, 45, 64, 0.65);
  padding-bottom: 1px;
  text-decoration: none;
  transition: color 0.2s ease;
}

.table-link:hover {
  color: #161b28;
}

.orders-modal-title {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-family: 'Roboto', 'Helvetica Neue', 'Segoe UI', sans-serif;
}

.orders-modal-title .title-label {
  color: #1f2940;
  font-size: 1.05rem;
  font-weight: 600;
  letter-spacing: 0.02em;
}

.orders-modal-title .title-meta {
  color: #5b6480;
  font-size: 0.74rem;
  text-transform: uppercase;
  letter-spacing: 0.12em;
}

::v-deep #orders-modal .modal-header {
  background: linear-gradient(135deg, #f4f6ff 0%, #e6e9ff 100%);
  border-bottom: none;
  padding: 18px 22px;
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
  box-shadow: inset 0 -1px 0 rgba(98, 111, 149, 0.12);
}

::v-deep #orders-modal .modal-header .close {
  color: #4b5fdd;
  opacity: 0.75;
  text-shadow: none;
  transition: opacity 0.2s ease, transform 0.2s ease;
}

::v-deep #orders-modal .modal-header .close:hover {
  opacity: 1;
  transform: scale(1.05);
}

.orders-mobile-list {
  display: flex;
  flex-direction: column;
  gap: 14px;
  max-height: 60vh;
  overflow-y: auto;
}

.order-card {
  border-radius: 14px;
  border: 1px solid rgba(229, 232, 240, 0.9);
  background: linear-gradient(135deg, #fbfcff, #f6f8ff);
  box-shadow: 0 6px 16px rgba(20, 29, 56, 0.08);
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  font-family: 'Roboto', 'Helvetica Neue', 'Segoe UI', sans-serif;
}

.order-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.order-number {
  font-weight: 600;
  color: #1f2940;
  letter-spacing: 0.01em;
}

.order-status {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  box-shadow: inset 0 0 0 1px rgba(98, 111, 149, 0.12);
}

.order-status.success {
  background: #e3f5ea;
  color: #1f7a4d;
}

.order-status.primary {
  background: #e5ebff;
  color: #3446a4;
}

.order-status.warning {
  background: #fff4df;
  color: #a76200;
}

.order-status.info {
  background: #e7f4ff;
  color: #0f5b97;
}

.order-status.danger {
  background: #ffe5ea;
  color: #a90021;
}

.order-status.secondary {
  background: #eef1fb;
  color: #4a5374;
}

.order-card-body {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.order-row {
  display: flex;
  justify-content: space-between;
  font-size: 0.82rem;
  color: #2a3144;
}

.order-label {
  font-weight: 500;
  color: #6a738c;
  text-transform: uppercase;
  font-size: 0.7rem;
  letter-spacing: 0.08em;
}

.order-value {
  font-weight: 500;
}

.order-price {
  color: #1f2940;
}

@media (max-width: 768px) {
  .modal-section {
    padding: 14px;
  }
}
</style>